import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import type { AppointmentRecord } from "@/lib/types/appointment";
import type { AppointmentRegistry } from "@/lib/appointment/AppointmentRegistry";
import type { StaffRegistry } from "@/lib/staff/StaffRegistry";
import { getAppointmentRegistry } from "@/lib/appointment/appointmentRegistrySingleton";
import { draftClientFor, takePendingBooking } from "@/lib/inbox/pendingBooking";

interface AppointmentCreateModalContextState {
  open: boolean;
  appointment: AppointmentRecord | null;
  setAppointment: (appt: AppointmentRecord | null) => void;
  updateAppointment: (changes: Partial<AppointmentRecord>) => void;
  openModal: (params: {
    staffId: number,
    staffObj?: any,
    date: string,
    startTime: string,
    endTime: string,
    company_id?: number,
    client?: any,
    services?: any[],
    comment?: string,
    staffRegistry: StaffRegistry,
  }) => void;
  closeModal: () => void;
  resetAppointment: () => void;
  appointmentRegistry: AppointmentRegistry | null;
  staffRegistry: StaffRegistry | null;
}

const AppointmentCreateModalContext = createContext<AppointmentCreateModalContextState | undefined>(undefined);

export function useAppointmentCreateModal() {
  const ctx = useContext(AppointmentCreateModalContext);
  if (!ctx) {
    throw new Error("useAppointmentCreateModal must be used within an AppointmentCreateModalProvider");
  }
  return ctx;
}

interface AppointmentCreateModalProviderProps {
  children: ReactNode;
}

export function AppointmentCreateModalProvider({ children }: AppointmentCreateModalProviderProps) {
  const [open, setOpen] = useState(false);
  const [appointment, setAppointment] = useState<AppointmentRecord | null>(null);
  const [initialAppointment, setInitialAppointment] = useState<AppointmentRecord | null>(null);
  const [appointmentRegistry, setAppointmentRegistry] = useState<AppointmentRegistry | null>(null);
  const [staffRegistry, setStaffRegistry] = useState<StaffRegistry | null>(null);

  // Open modal and initialize draft appointment
  const openModal = useCallback((params: {
    staffId: number,
    staffObj?: any,
    date: string,
    startTime: string,
    endTime: string,
    company_id?: number,
    client?: any,
    services?: any[],
    comment?: string,
    staffRegistry: StaffRegistry,
  }) => {
    const singletonRegistry = getAppointmentRegistry();
    setAppointmentRegistry(singletonRegistry);
    setStaffRegistry(params.staffRegistry);
    // "New booking" from the Boona Direct chat: the client waiting for a slot
    // (src/lib/inbox/pendingBooking.ts) comes with the first slot clicked
    const waiting = params.client ? null : takePendingBooking();
    const draft = singletonRegistry.createDraftAppointment({
      staffId: params.staffId,
      staffObj: params.staffObj,
      date: params.date,
      startTime: params.startTime,
      endTime: params.endTime,
      company_id: params.company_id,
      client: params.client ?? (waiting ? draftClientFor(waiting) : undefined),
      services: params.services,
      comment: params.comment,
    });
    setAppointment(draft);
    setInitialAppointment(draft);
    setOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
    setAppointment(null);
    setInitialAppointment(null);
    setAppointmentRegistry(null);
    setStaffRegistry(null);
  }, []);

  // Update appointment with partial changes (deep merge for nested objects)
  const updateAppointment = useCallback((changes: Partial<AppointmentRecord>) => {
    setAppointment((prev) => {
      if (!prev) return prev;
      const merged: any = { ...prev }; // Fixed: was { .prev }
      for (const key in changes) {
        if (
          typeof changes[key] === "object" &&
          changes[key] !== null &&
          !Array.isArray(changes[key]) &&
          typeof merged[key] === "object" &&
          merged[key] !== null &&
          !Array.isArray(merged[key])
        ) {
          merged[key] = { ...merged[key], ...(changes as any)[key] }; // Fixed: was { .merged[key], .changes[key] }
        } else {
          merged[key] = changes[key];
        }
      }
      // Logging (match AppointmentModalContext)
      console.log("[AppointmentCreateModalContext] updateAppointment called with changes:", changes);
      console.log("[AppointmentCreateModalContext] new appointment object:", merged);
      return merged;
    });
  }, []);

  // Reset appointment to initial state
  const resetAppointment = useCallback(() => {
    setAppointment(initialAppointment);
  }, [initialAppointment]);

  const value: AppointmentCreateModalContextState = {
    open,
    appointment,
    setAppointment,
    updateAppointment,
    openModal,
    closeModal,
    resetAppointment,
    appointmentRegistry,
    staffRegistry,
  };

  return (
    <AppointmentCreateModalContext.Provider value={value}>
      {children}
    </AppointmentCreateModalContext.Provider>
  );
}
